import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";
import { ACTIVE_STAGES, TERMINAL_STAGES, STAGE_LABELS } from "./stages";
import { ComplianceBadge } from "./atoms";

export default function TargetPipeline() {
  const navigate = useNavigate();
  const [byStage, setByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [drag, setDrag] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: pipe }] = await Promise.all([
        api.get("/cdl/targets?limit=500"),
        api.get("/cdl/pipeline"),
      ]);
      const grouped = {};
      (list.items || []).forEach((t) => {
        const s = t.stage || "repere";
        (grouped[s] ||= []).push(t);
      });
      setByStage(grouped);
      setCounts(pipe.counts || {});
    } catch (e) {
      toast.error("Impossible de charger le pipeline.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dropTo = async (stage) => {
    if (!drag || drag.stage === stage) { setDrag(null); return; }
    try {
      await api.post(`/cdl/targets/${drag.target_id}/stage`, { stage });
      toast.success(`Déplacé → ${STAGE_LABELS[stage]}`);
      load();
    } catch (e) {
      toast.error("Déplacement impossible.");
    } finally {
      setDrag(null);
    }
  };

  return (
    <IntelligenceHub activeTab="pipeline">
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#5B5B62]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} /> Chargement du pipeline…
          </div>
        ) : (
          <>
            <div className="text-xs text-[#5B5B62]">
              Glissez une carte d&apos;une colonne à l&apos;autre pour changer d&apos;étape. Toute modification est journalisée.
            </div>
            <div className="flex gap-3 overflow-x-auto pb-4">
              {ACTIVE_STAGES.map((s) => (
                <Column
                  key={s}
                  stage={s}
                  targets={byStage[s] || []}
                  count={counts[s] || 0}
                  onCard={(t) => navigate(`/app/commercial/list?target=${t.target_id}`)}
                  onDragStart={(t) => setDrag(t)}
                  onDrop={() => dropTo(s)}
                />
              ))}
            </div>

            <div className="mt-8 border-t border-[#E4E1D8] pt-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] mb-3">Archives</div>
              <div className="flex gap-3 overflow-x-auto">
                {TERMINAL_STAGES.map((s) => (
                  <Column
                    key={s}
                    stage={s}
                    targets={byStage[s] || []}
                    count={counts[s] || 0}
                    onCard={(t) => navigate(`/app/commercial/list?target=${t.target_id}`)}
                    onDragStart={(t) => setDrag(t)}
                    onDrop={() => dropTo(s)}
                    dim
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </IntelligenceHub>
  );
}

function Column({ stage, targets, count, onCard, onDragStart, onDrop, dim }) {
  return (
    <div
      data-testid={`intel-col-${stage}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`shrink-0 w-72 rounded-md border ${dim ? "border-dashed" : ""} border-[#E4E1D8] bg-[#F8F7F3] flex flex-col`}
    >
      <div className="px-3 py-2.5 border-b border-[#E4E1D8] flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545]">{STAGE_LABELS[stage]}</div>
        <div className="text-[10px] font-mono text-[#8B8B8F]">{count}</div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
        {targets.length === 0 && <div className="text-[11px] text-[#8B8B8F] px-2 py-3">—</div>}
        {targets.map((t) => (
          <div
            key={t.target_id}
            draggable
            onDragStart={() => onDragStart(t)}
            onClick={() => onCard(t)}
            data-testid={`intel-card-${t.target_id}`}
            className="rounded-md border border-[#E4E1D8] bg-white p-3 hover:border-[#0B2545] transition-colors cursor-grab active:cursor-grabbing"
          >
            <div className="text-sm font-medium text-[#1F2937] truncate">{t.display_name}</div>
            <div className="text-[11px] text-[#5B5B62] truncate">{[t.organization_name, t.role_or_title].filter(Boolean).join(" · ")}</div>
            <div className="mt-2 flex items-center justify-between">
              <ComplianceBadge status={t.compliance_status} />
              {t.ai_score != null && <span className="text-[11px] font-semibold text-[#0B2545]">{t.ai_score}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
